import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Check, UserPlus, X } from 'lucide-react';
import { useMembers, RELATION_LABEL } from '../lib/members';
import { store } from '../lib/store';
import { useToast } from '../components/Toast';
import { Eyebrow } from '../components/ui';
import type { MemberRelation } from '@shared/types';

const RELATION_OPTIONS: { value: MemberRelation; label: string }[] = [
  { value: 'spouse', label: 'Coniuge' },
  { value: 'child', label: 'Figlio' },
  { value: 'parent', label: 'Genitore' },
  { value: 'other', label: 'Altro' },
];

export function FamiliariScreen() {
  const navigate = useNavigate();
  const { members, activeId, setActive, reload, loading } = useMembers();
  const { show } = useToast();
  const [adding, setAdding] = useState(false);

  return (
    <div className="screen">
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="topbar__title">Familiari</div>
        <button
          className="iconbtn"
          style={{ background: 'var(--accent)', color: 'var(--atext)', border: 'none' }}
          onClick={() => setAdding(true)}
          aria-label="Aggiungi familiare"
        >
          <Plus size={20} />
        </button>
      </div>

      <Eyebrow>Profilo attivo</Eyebrow>
      <div className="list" style={{ marginTop: 11 }}>
        {loading && members.length === 0 ? (
          <div className="skeleton" style={{ height: 64 }} />
        ) : (
          members.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`memberrow${m.id === activeId ? ' is-active' : ''}`}
              onClick={() => setActive(m.id)}
            >
              <span className="avatar">{m.display_name.charAt(0).toUpperCase()}</span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div className="memberrow__name">{m.display_name}</div>
                <div className="memberrow__meta">
                  {RELATION_LABEL[m.relation ?? 'other']} · {m.doc_count} element{m.doc_count === 1 ? 'o' : 'i'}
                </div>
              </div>
              {m.id === activeId && <Check size={20} style={{ color: 'var(--accent)' }} />}
            </button>
          ))
        )}

        {/* Aggiungi familiare (dashed) */}
        <button type="button" className="memberrow memberrow--add" onClick={() => setAdding(true)}>
          <span className="iconchip iconchip--sm"><UserPlus size={18} /></span>
          <span style={{ flex: 1, textAlign: 'left', color: 'var(--muted)' }}>Aggiungi familiare</span>
        </button>
      </div>

      {adding && (
        <AddMemberSheet
          onClose={() => setAdding(false)}
          onDone={async (name, relation) => {
            try {
              await store.createMember({ display_name: name, relation });
              setAdding(false);
              reload();
              show('Familiare aggiunto');
            } catch {
              show('Operazione non riuscita');
            }
          }}
        />
      )}
    </div>
  );
}

function AddMemberSheet({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (name: string, relation: MemberRelation) => void;
}) {
  const [name, setName] = useState('');
  const [relation, setRelation] = useState<MemberRelation>('spouse');

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />
        <div className="sheet__head">
          <div style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>Nuovo familiare</div>
          <button className="iconbtn" onClick={onClose} aria-label="Chiudi" style={{ width: 34, height: 34 }}>
            <X size={18} />
          </button>
        </div>

        <label className="field__label" style={{ marginTop: 8 }}>Nome e cognome</label>
        <input
          className="field__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Es. Anna Rossi"
          autoFocus
        />

        <label className="field__label" style={{ marginTop: 14 }}>Relazione</label>
        <div className="chips">
          {RELATION_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`chip${relation === o.value ? ' is-active' : ''}`}
              onClick={() => setRelation(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>

        <button
          className="btn btn--accent"
          style={{ width: '100%', marginTop: 18 }}
          disabled={!name.trim()}
          onClick={() => onDone(name.trim(), relation)}
        >
          Aggiungi familiare
        </button>
      </div>
    </div>
  );
}
